import { useRef, useState } from 'react';
import { Camera, RefreshCw, Upload } from 'lucide-react';

export interface CapturedPhoto {
  file: File;
  dataUrl: string;
}

interface PhotoCaptureProps {
  /** Called when a photo is captured/chosen, or null when cleared. */
  onChange: (photo: CapturedPhoto | null) => void;
  /** Shown inside the empty drop zone, e.g. "your pilot certificate". */
  subject: string;
  /** Disable while a parent action (e.g. AI read) is in flight. */
  busy?: boolean;
  /** A previously-saved image URL to show on resume (already uploaded). */
  initialPreview?: string;
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * Photo capture for onboarding documents. On phones/iPads `capture="environment"`
 * opens the rear camera directly; on desktop the same input is a file picker.
 * Two separate inputs so we can offer "Take photo" (camera) and "Choose file"
 * (library/desktop) as distinct affordances.
 */
export function PhotoCapture({ onChange, subject, busy, initialPreview }: PhotoCaptureProps) {
  const cameraInput = useRef<HTMLInputElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(initialPreview ?? null);
  const [err, setErr] = useState<string | null>(null);

  async function handleFile(file: File | undefined) {
    setErr(null);
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      setErr('That image is over 15MB — try again.');
      return;
    }
    try {
      const dataUrl = await readAsDataUrl(file);
      setPreview(dataUrl);
      onChange({ file, dataUrl });
    } catch {
      setErr('Could not read that image. Try again.');
    }
  }

  function reset() {
    setPreview(null);
    setErr(null);
    onChange(null);
    if (cameraInput.current) cameraInput.current.value = '';
    if (fileInput.current) fileInput.current.value = '';
  }

  return (
    <div>
      <input
        ref={cameraInput}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      <input
        ref={fileInput}
        type="file"
        accept="image/*,application/pdf"
        className="sr-only"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      {preview ? (
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <img
            src={preview}
            alt={`Photo of ${subject}`}
            className="mx-auto max-h-64 rounded-lg object-contain"
          />
          <button
            type="button"
            onClick={reset}
            disabled={busy}
            className="btn-secondary mt-3 inline-flex items-center gap-2"
          >
            <RefreshCw size={16} /> Retake
          </button>
        </div>
      ) : (
        <div className="rounded-xl border-2 border-dashed border-sky-200 bg-sky-50/60 p-6 text-center">
          <Camera className="mx-auto mb-2 text-navy-700" size={28} />
          <p className="text-sm text-ink-muted mb-4">
            Take a clear, well-lit photo of {subject}.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={() => cameraInput.current?.click()}
              disabled={busy}
              className="btn-primary inline-flex items-center gap-2 min-h-[44px]"
            >
              <Camera size={18} /> Take photo
            </button>
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={busy}
              className="btn-secondary inline-flex items-center gap-2 min-h-[44px]"
            >
              <Upload size={18} /> Choose file
            </button>
          </div>
        </div>
      )}

      {err && <p className="mt-2 text-sm text-red-700">{err}</p>}
    </div>
  );
}
